import React from "react";
import { Modal, Table, Button, Tooltip, Tag, message } from "antd";
import type { ColumnsType, TablePaginationConfig, TableProps } from "antd/es/table";
import { CopyOutlined, StopOutlined } from "@ant-design/icons";
import type { RecordTaskItem, ListRecordTasksParams } from "../api";

/**
 * 历史录制任务弹窗组件 Props
 */
export interface HistoryRecordTasksProps {
  open: boolean;
  loading: boolean;
  data: RecordTaskItem[];
  pagination: TablePaginationConfig;
  onCancel: () => void;
  onPageChange: (page: number, pageSize: number) => void;
  onStopTask: (taskId: string) => void;
  onCopyTaskId: (taskId: string) => void;
}

/**
 * 历史录制任务弹窗组件
 * @param props - 组件属性
 * @returns React 元素
 */
const HistoryRecordTasks: React.FC<HistoryRecordTasksProps> = (props) => {
  const { open, loading, data, pagination, onCancel, onPageChange, onStopTask, onCopyTaskId } = props;

  /**
   * 历史任务表格列定义
   */
  const columns: ColumnsType<RecordTaskItem> = [
    {
      title: "任务 ID",
      dataIndex: "task_id",
      key: "task_id",
      render: (taskId: string) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={taskId}>
            <span
              style={{
                flexGrow: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                marginRight: 8,
              }}
            >
              {taskId}
            </span>
          </Tooltip>
          <Tooltip title="复制任务 ID">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onCopyTaskId(taskId);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: "产品线",
      dataIndex: "business",
      key: "business",
    },
    {
      title: "环境",
      dataIndex: "cluster_env",
      key: "cluster_env",
    },
    {
      title: "环境名称",
      dataIndex: "env_name",
      key: "env_name",
      render: (envName: string, record: RecordTaskItem) => (
        <Tooltip title={record.env_description || "暂无描述"}>
          <span>{envName}</span>
        </Tooltip>
      ),
    },
    {
      title: "请求头配置",
      dataIndex: "request_headers",
      key: "request_headers",
      render: (headers?: Record<string, string>) => {
        if (!headers || Object.keys(headers).length === 0) {
          return <span>-</span>;
        }
        const headerText = Object.entries(headers)
          .map(([key, value]) => `${key}: ${value}`)
          .join("\n");
        return (
          <Tooltip title={<pre style={{ margin: 0 }}>{headerText}</pre>}>
            <Tag color="blue">{Object.keys(headers).length} 个请求头</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      render: (ts: string) => {
        if (!ts) return "-";
        const date = new Date(ts);
        return isNaN(date.getTime()) ? "无效日期" : date.toLocaleString();
      },
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status: boolean) =>
        status ? <Tag color="default">已结束</Tag> : <Tag color="processing">进行中</Tag>,
    },
    {
      title: "操作",
      key: "action",
      align: "center",
      render: (_: unknown, record: RecordTaskItem) =>
        !record.status ? (
          <Tooltip title="停止此录制任务">
            <Button
              danger
              size="small"
              icon={<StopOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onStopTask(record.task_id);
              }}
            />
          </Tooltip>
        ) : null,
    },
  ];

  /**
   * 处理分页变化
   * @param paginationConfig - 分页配置
   */
  const handleTableChange: TableProps<RecordTaskItem>["onChange"] = (paginationConfig) => {
    onPageChange(paginationConfig.current || 1, paginationConfig.pageSize || 10);
  };

  return (
    <Modal
      title="历史录制任务"
      open={open}
      onCancel={onCancel}
      footer={null}
      width="70%"
      destroyOnClose
    >
      <Table<RecordTaskItem>
        rowKey={record => record.task_id || `${record.cluster_env}-${record.created_at}`}
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        size="small"
      />
    </Modal>
  );
};

export default HistoryRecordTasks; 